import React, { useState } from 'react';
import { QuickLink } from '../types';
import { PlusIcon } from './icons/PlusIcon';
import { TrashIcon } from './icons/TrashIcon';
import { LinkIcon } from './icons/LinkIcon';
import AddButtonPop from './AddButtonPop';

interface LinkListProps {
    links: QuickLink[];
    onDelete: (id: string) => void;
    onAdd?: (title: string, url: string) => void;
    onOpenModal?: () => void;
    isAdmin?: boolean;
    addButtonText?: string;
    emptyText?: string;
    maxHeight?: string;
}

const LinkList: React.FC<LinkListProps> = ({
    links,
    onDelete,
    onAdd,
    onOpenModal,
    isAdmin,
    addButtonText = 'Add Link',
    emptyText = 'No links yet. Click + to add.',
    maxHeight = 'max-h-48',
}) => {
    const [isAdding, setIsAdding] = useState(false);

    return (
        <div className="relative">
            {isAdmin && (
                <AddButtonPop
                    isOpen={isAdding}
                    onClick={() => setIsAdding(!isAdding)}
                    onConfirm={(title, url) => {
                        if (onAdd) {
                            onAdd(title, url);
                        } else if (onOpenModal) {
                            onOpenModal();
                        }
                    }}
                    isAdmin={isAdmin}
                    buttonText={addButtonText}
                    titlePlaceholder="Name"
                    urlPlaceholder="URL"
                />
            )}

            <div className={`space-y-2 ${maxHeight} overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent`}>
                {links.map((link) => (
                    <div key={link.id} className="flex items-center justify-between bg-white/50 p-2 rounded-md group">
                        <a
                            href={link.url.startsWith('http') ? link.url : `https://${link.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-2 text-light hover:text-accent transition-colors truncate"
                        >
                            {link.favicon ? (
                                <img src={link.favicon} alt="" className="w-4 h-4 flex-shrink-0 rounded-[4px]" />
                            ) : (
                                <LinkIcon className="w-4 h-4 flex-shrink-0" />
                            )}
                            <span className="sidebar-item-link" title={link.title}>{link.title}</span>
                        </a>
                        {isAdmin && (
                            <button
                                onClick={() => onDelete(link.id)}
                                className="text-medium hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                aria-label={`Delete ${link.title}`}
                            >
                                <TrashIcon className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                ))}
                {links.length === 0 && isAdmin && !isAdding && (
                    <div className="text-center py-4 text-gray-400 text-xs flex flex-col items-center">
                        <PlusIcon className="w-8 h-8 mb-2 opacity-50" />
                        <span>{emptyText}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LinkList;
